import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Maximize, CheckCircle, X } from 'lucide-react';
import './ProjectDetail.css';
import useSEO from '../hooks/useSEO';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
        const response = await fetch(`${API_BASE}/projects.php`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        const result = JSON.parse(text);
        if (result.success) {
          const projects = result.data || [];
          // Try multiple ID matching strategies
          const foundProject = projects.find(p => {
            const projectId = p.id;
            const urlId = id;
            // Try exact match
            if (projectId == urlId) return true;
            // Try parseInt comparison
            if (parseInt(projectId) === parseInt(urlId)) return true;
            // Try string comparison
            if (String(projectId) === String(urlId)) return true;
            return false;
          });
          
          if (!foundProject) {
            console.error('Project not found. ID:', id, 'Available IDs:', projects.map(p => p.id));
          }
          
          setProject(foundProject);
        } else {
          console.error('Failed to fetch project:', result.error);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  useSEO({
    title: project 
      ? `${project.name} - Efruz İnşaat Projesi | ${project.type}`
      : 'Proje Detayı - Efruz İnşaat',
    description: project
      ? `${project.name} projesi hakkında detaylı bilgi. ${project.location || ''} konumunda ${project.year || ''} yılında tamamlanan ${project.type || ''} projesi. ${project.description?.substring(0, 120) || ''}...`
      : 'Efruz İnşaat proje detay sayfası',
    keywords: project
      ? `${project.name}, ${project.type}, ${project.location}, efruz inşaat projesi, inşaat proje detayı, ${project.category} projeleri`
      : 'efruz inşaat, proje detayı',
    image: project?.image
  });

  if (loading) {
    return (
      <div className="project-loading">
        <div className="container">
          <div className="loading-spinner">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-not-found">
        <div className="container">
          <h1 className="heading-1">Proje bulunamadı</h1>
          <Link to="/projects" className="btn-primary">
            <ArrowLeft size={18} />
            Projelere Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      {/* Hero Image */}
      <section className="project-hero">
        <img src={project.image} alt={project.name} className="project-hero-image" />
        <div className="project-hero-overlay"></div>
        <div className="container">
          <div className="project-hero-content">
            <Link to="/projects" className="back-link">
              <ArrowLeft size={20} />
              <span>Projelere Dön</span>
            </Link>
            <h1 className="hero-large">{project.name}</h1>
            <span className="project-category">{project.type}</span>
          </div>
        </div>
      </section>

      {/* Project Info */}
      <section className="project-info-section section-padding">
        <div className="container">
          <div className="project-info-grid">
            {/* Details */}
            <div className="project-details">
              <h2 className="heading-2">Proje Detayları</h2>
              <p className="body-large project-description">{project.description}</p>
              
              <div className="project-specs">
                {project.location && (
                  <div className="spec-item">
                    <MapPin size={24} />
                    <div>
                      <h4 className="body-small">Konum</h4>
                      <p className="body-regular">{project.location}</p>
                    </div>
                  </div>
                )}
                {project.size && (
                  <div className="spec-item">
                    <Maximize size={24} />
                    <div>
                      <h4 className="body-small">Alan</h4>
                      <p className="body-regular">{project.size}</p>
                    </div>
                  </div>
                )}
                {project.year && (
                  <div className="spec-item">
                    <Calendar size={24} />
                    <div>
                      <h4 className="body-small">Tamamlanma</h4>
                      <p className="body-regular">{project.year}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Features */}
            {project.features && project.features.length > 0 && (
              <div className="project-features">
                <h3 className="heading-3">Özellikler</h3>
                <ul className="features-list">
                  {project.features.map((feature, index) => (
                    <li key={index} className="feature-item">
                      <CheckCircle size={20} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {project.gallery && project.gallery.length > 0 && (
        <section className="project-gallery section-padding">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">Proje Galerisi</h2>
              <p className="body-large">Projemizden görüntüler</p>
            </div>
            <div className="gallery-grid">
              {project.gallery.map((image, index) => (
                <div 
                  key={index} 
                  className="gallery-item"
                  onClick={() => setSelectedImage(image)}
                >
                  <img src={image} alt={`${project.name} - Görsel ${index + 1}`} />
                  <div className="gallery-overlay">
                    <span className="gallery-view-text">Görüntüle</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <button className="modal-close" onClick={() => setSelectedImage(null)}>
            <X size={32} />
          </button>
          <img src={selectedImage} alt={project.name} className="modal-image" />
        </div>
      )}

      {/* CTA */}
      <section className="project-cta section-padding-small">
        <div className="container">
          <div className="cta-box">
            <h2 className="heading-2">Benzer bir projeye mi ihtiyacınız var?</h2>
            <p className="body-large">Hayalinizdeki projeyi birlikte gerçekleştirelim</p>
            <Link to="/contact" className="btn-primary">
              İletişime Geçin
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
